'use client';
import React from 'react';
import PropTypes from 'prop-types';
import { interpolate } from '@format/interpolate';
import { useSearchParams } from 'next/navigation';
import { Eyebrow, PillText } from '@web/atoms';
import { Typewriter } from '@web/atoms/Typewriter';
import { CtaButton, IMAGE_SIZES, ResponsiveImage } from '@web/molecules';
import { useReporter } from '../../reporter';
import { useShopifyOfferContext } from '../../offer/shopifyContext';
import { MuxVideo } from '../MuxVideo';
import { CTAGroup } from '../CTAGroup';
import { EmailCapture } from '../EmailCaptureBar/EmailCapture';
import { heroButtonClicked } from '../Hero/events';
import { HeroStats } from '../HeroStats';
import { HeroBadges } from '../HeroBadges';
import {
  StyledHeroSlide,
  BackgroundImage,
  BackgroundVideo,
  BackgroundGradientOverlay,
  ContentContainer,
  PillWrapper,
  EyebrowWrapper,
  Headline,
  Subheadline,
  CtaWrapper,
  HeroSlideCtaShell,
  InputWrapper,
  AccessoryContainer,
  FullContentWrapper,
} from './styles.js';

export const HeroSlide = ({
  backgroundAsset,
  pill,
  eyebrow,
  headline,
  headlineVariants,
  subheadline,
  cta,
  accessoryComponent,
  backgroundColor = 'background.dark',
  fontColor = 'text.lightest',
  typography = 'headingMastiff',
  subheadlineTypography = 'largeBody',
}) => {
  const reporter = useReporter();
  const searchParams = useSearchParams();
  const { formattedAmount } = useShopifyOfferContext();
  const headlineVariant = searchParams?.get('hpkeyword');

  if (headlineVariant && headlineVariants) {
    const variant = headlineVariants.find(e => e.variant === headlineVariant);
    if (variant) {
      headline = variant.heading;
    }
  }

  const interpolateOffer = value =>
    typeof value === 'string'
      ? interpolate(value, { offer: formattedAmount })
      : value;

  return (
    <StyledHeroSlide $backgroundColor={backgroundColor} $fontColor={fontColor}>
      {backgroundAsset?.component === 'mux_video' ? (
        <BackgroundVideo>
          <MuxVideo
            autoplay
            loop
            {...backgroundAsset}
            parentHeading={headline}
          />
        </BackgroundVideo>
      ) : (
        backgroundAsset && (
          <BackgroundImage>
            <ResponsiveImage
              {...backgroundAsset}
              priority
              objectFit="cover"
              sizes={IMAGE_SIZES.fullBleed}
            />
          </BackgroundImage>
        )
      )}
      {backgroundAsset && <BackgroundGradientOverlay />}
      <FullContentWrapper>
        <ContentContainer>
          {pill && (
            <PillWrapper>
              <PillText {...pill} text={interpolateOffer(pill.text)} />
            </PillWrapper>
          )}
          {eyebrow && (
            <EyebrowWrapper>
              {typeof eyebrow === 'string' ? (
                <Eyebrow fontColor={fontColor}>
                  {interpolateOffer(eyebrow)}
                </Eyebrow>
              ) : eyebrow.component === 'typewriter' ? (
                <Typewriter {...eyebrow} />
              ) : (
                <Eyebrow fontColor={fontColor}>
                  {interpolateOffer(eyebrow.text ?? eyebrow)}
                </Eyebrow>
              )}
            </EyebrowWrapper>
          )}
          {headline && (
            <Headline $fontColor={fontColor} $typography={typography}>
              {headline}
            </Headline>
          )}
          {subheadline && (
            <Subheadline
              $fontColor={fontColor}
              $typography={subheadlineTypography}
            >
              {subheadline}
            </Subheadline>
          )}
          {cta && (
            <CtaWrapper $alignment={cta.alignment}>
              {cta.component === 'email_capture' ? (
                <InputWrapper>
                  <EmailCapture {...cta} buttonVariant={cta.variant} />
                </InputWrapper>
              ) : cta.component === 'cta_group' ? (
                <CTAGroup {...cta} />
              ) : (
                <HeroSlideCtaShell
                  alignment={cta.alignment}
                  fullWidth={cta.fullWidth}
                  fullWidthMobile={cta.fullWidthMobile}
                  fullWidthDesktop={cta.fullWidthDesktop}
                >
                  <CtaButton
                    {...cta}
                    href={cta.target}
                    variant={cta.variant}
                    icon={cta.icon}
                    iconAlignment={cta.iconAlignment}
                    headerText={cta.headerText}
                    triggerModal={cta.triggerModal}
                    modal={cta.modal}
                    onClick={() =>
                      reporter.tag(heroButtonClicked(cta.text, cta.target))
                    }
                  >
                    {cta.text}
                  </CtaButton>
                </HeroSlideCtaShell>
              )}
            </CtaWrapper>
          )}
        </ContentContainer>
        {accessoryComponent && (
          <AccessoryContainer>
            {accessoryComponent.component === 'hero_stats' ? (
              <HeroStats {...accessoryComponent} transparent />
            ) : (
              <HeroBadges {...accessoryComponent} />
            )}
          </AccessoryContainer>
        )}
      </FullContentWrapper>
    </StyledHeroSlide>
  );
};

HeroSlide.displayName = 'HeroSlide';

HeroSlide.propTypes = {
  backgroundAsset: PropTypes.object,
  pill: PropTypes.object,
  eyebrow: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.node,
  ]),
  headline: PropTypes.node,
  headlineVariants: PropTypes.array,
  subheadline: PropTypes.node,
  cta: PropTypes.object,
  accessoryComponent: PropTypes.shape({
    component: PropTypes.oneOf(['hero_stats', 'hero_badges']).isRequired,
  }),
  backgroundColor: PropTypes.string,
  fontColor: PropTypes.string,
  typography: PropTypes.string,
  subheadlineTypography: PropTypes.string,
};
